// Lightweight API client for backend interaction
// Centralizes base URL, auth header, and helpers

import { Plan, PaymentRequestData, PaymentResponseData } from "../types/plan";

// Re-export Plan type for convenience
export type { Plan };

const BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

// Request deduplication cache
const requestCache = new Map<string, Promise<any>>();

export interface ApiOptions extends RequestInit {
	auth?: boolean;
}

export interface ApiResult<T> {
	success: boolean;
	data?: T;
	error?: string;
}

function getAuthToken(): string | null {
	try {
		return localStorage.getItem("authToken");
	} catch {
		return null;
	}
}

// Nova função que retorna resultado sem lançar exceções
export async function apiRequest<T>(path: string, options: ApiOptions = {}): Promise<ApiResult<T>> {
	try {
		const url = `${BASE_URL}${path}`;
		const headers: Record<string, string> = {};

		// Copy headers from options if they exist
		if (options.headers) {
			Object.entries(options.headers).forEach(([key, value]) => {
				if (typeof value === "string") {
					headers[key] = value;
				}
			});
		}

		// Only set Content-Type for JSON requests, not for FormData
		if (!(options.body instanceof FormData)) {
			headers["Content-Type"] = "application/json";
		}

		if (options.auth) {
			const token = getAuthToken();
			if (token) {
				headers["Authorization"] = `Bearer ${token}`;
			}
		}

		const response = await fetch(url, {
			...options,
			headers,
		});

		if (!response.ok) {
			if (response.status === 401) {
				localStorage.removeItem("authToken");
				localStorage.removeItem("currentUser");
				localStorage.removeItem("currentOrganization");
				window.location.href = "/login";
				return {
					success: false,
					error: "Sessão expirada. Faça login novamente.",
				};
			}

			let errorMessage = `Request failed with status ${response.status}`;
			try {
				const errorData = await response.json();
				if (errorData.message) {
					errorMessage = errorData.message;
				}
			} catch {
				const text = await response.text().catch(() => "");
				if (text) {
					errorMessage = text;
				}
			}

			return {
				success: false,
				error: errorMessage,
			};
		}

		// Handle empty responses
		const contentType = response.headers.get("content-type");
		if (!contentType || !contentType.includes("application/json")) {
			if (
				path.includes("[]") ||
				path.includes("GetAll") ||
				path.includes("GetBy") ||
				path.includes("GetSales") ||
				path.includes("GetConversations")
			) {
				return { success: true, data: [] as T };
			}
			return { success: true, data: {} as T };
		}

		const data = await response.json();
		return { success: true, data };
	} catch (error: any) {
		return {
			success: false,
			error: error.message || "Network error occurred",
		};
	}
}

// Função antiga mantida para compatibilidade
export async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
	// Don't cache POST, PUT, PATCH, DELETE requests
	const method = options.method || "GET";
	const shouldCache = method === "GET";

	// Create a unique cache key for this request
	const cacheKey = `${method}:${path}:${JSON.stringify(options.body || "")}`;

	// Check if this exact request is already in progress (only for GET requests)
	if (shouldCache && requestCache.has(cacheKey)) {
		return requestCache.get(cacheKey)!;
	}

	const requestPromise = (async () => {
		const url = `${BASE_URL}${path}`;
		const headers: HeadersInit = {
			"Content-Type": "application/json",
			...(options.headers || {}),
		};

		if (options.auth) {
			const token = getAuthToken();
			if (token) {
				(headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
			}
		}

		const response = await fetch(url, {
			...options,
			headers,
		});

		if (!response.ok) {
			if (response.status === 401) {
				localStorage.removeItem("authToken");
				localStorage.removeItem("currentUser");
				localStorage.removeItem("currentOrganization");
				window.location.href = "/login";
				return Promise.reject({
					isApiError: true,
					message: "Sessão expirada. Faça login novamente.",
					status: response.status,
				});
			}

			let errorMessage = `Request failed with status ${response.status}`;
			try {
				const errorData = await response.json();
				if (errorData.message) {
					errorMessage = errorData.message;
				}
			} catch {
				// Se não conseguir fazer parse do JSON, usar o texto simples
				const text = await response.text().catch(() => "");
				if (text) {
					errorMessage = text;
				}
			}

			// Retornar um objeto de erro em vez de lançar exceção
			// Isso evita a tela vermelha de erro do React
			return Promise.reject({
				isApiError: true,
				message: errorMessage,
				status: response.status,
			});
		}

		// Handle empty responses
		const contentType = response.headers.get("content-type");
		if (!contentType || !contentType.includes("application/json")) {
			// For non-JSON responses, return an empty object or array based on expected type
			if (
				path.includes("[]") ||
				path.includes("GetAll") ||
				path.includes("GetBy") ||
				path.includes("GetSales") ||
				path.includes("GetConversations")
			) {
				return [] as T;
			}
			return {} as T;
		}
		return (await response.json()) as T;
	})();

	// Cache the request promise (only for GET requests)
	if (shouldCache) {
		requestCache.set(cacheKey, requestPromise);

		// Clean up the cache after the request completes
		requestPromise.finally(() => {
			requestCache.delete(cacheKey);
		});
	}

	return requestPromise;
}

// Notification API endpoints
export interface Notification {
	id: string;
	title: string;
	message: string;
	userId: string;
	leadId?: string;
	conversationId?: string;
	type: string;
	isRead: boolean;
	createdAt: string;
}

export async function fetchNotifications(): Promise<Notification[]> {
	return apiFetch<Notification[]>("/api/notification", { auth: true });
}

export async function fetchUnreadCount(): Promise<{ count: number }> {
	return apiFetch<{ count: number }>("/api/notification/unread-count", { auth: true });
}

export async function markNotificationAsRead(notificationId: string): Promise<void> {
	return apiFetch<void>(`/api/notification/${notificationId}/mark-read`, {
		method: "PATCH",
		auth: true,
	});
}

// Auth endpoints
export interface LoginResponse {
	id: string;
	name: string;
	username: string;
	token: string;
	shouldResetPassword?: boolean;
	shouldRedirectWelcome?: boolean;
	user?: {
		id: string;
		name: string;
		email: string;
		username: string;
		phone: string;
		role: string;
		shouldResetPassword: boolean;
		isAdmin: boolean;
		isWhiteLabel?: boolean;
		managedOrganizationIds?: string[];
	};
}

export interface LoggedUserRS {
	uid: string;
	organizationName?: string | null;
	organizationId?: string | null;
	role: string; // expects: "admin" | "manager" | "sales" | "whitelabel" (lowercase)
	data: {
		displayName: string;
		email: string;
		phone: string;
		photoURL: string;
		settings: Record<string, string>;
		loginRedirectUrl: string;
	};
	shouldResetPassword?: boolean;
	shouldRedirectWelcome?: boolean;
	isSuperAdmin?: boolean;
	isWhiteLabel?: boolean;
	managedOrganizationIds?: string[];
	isPlanActive?: boolean;
}

export async function loginRequest(email: string, password: string, organizationName?: string): Promise<LoginResponse> {
	return apiFetch<LoginResponse>("/Authentication/SignIn", {
		method: "POST",
		body: JSON.stringify({ email, password }),
	});
}

export interface Organization {
	id: string;
	name: string;
	identifier: string;
	active: boolean;
	currentPlan?: string;
	endIn?: string;
	subscriptionActive?: boolean;
}

export async function getUserOrganizations(): Promise<Organization[]> {
	return apiFetch<Organization[]>("/Authentication/GetUserOrganizations", {
		method: "GET",
		auth: true,
	});
}

export async function searchOrganizations(name?: string): Promise<Organization[]> {
	const query = name ? `?name=${encodeURIComponent(name)}` : "";
	return apiFetch<Organization[]>(`/Organization/Search${query}`, {
		method: "GET",
		auth: true,
	});
}

export async function switchOrganization(organizationId: string): Promise<LoginResponse> {
	return apiFetch<LoginResponse>("/Authentication/SwitchOrganization", {
		method: "POST",
		auth: true,
		body: JSON.stringify({ organizationId }),
	});
}

export async function completeWelcome(): Promise<{ message: string }> {
	return apiFetch<{ message: string }>("/Authentication/CompleteWelcome", {
		method: "POST",
		auth: true,
	});
}

export async function validateEmail(email: string): Promise<{ exists: boolean; message?: string }> {
	try {
		const result = await apiFetch<{ exists: boolean; message?: string }>("/Authentication/ValidateEmail", {
			method: "POST",
			body: JSON.stringify({ email }),
		});
		return result;
	} catch (e: any) {
		if (e?.isApiError) {
			return { exists: true, message: e.message };
		}
		return { exists: false };
	}
}

export async function validateOrganizationName(
	organizationName: string
): Promise<{ exists: boolean; message?: string }> {
	try {
		const result = await apiFetch<{ exists: boolean; message?: string }>(
			"/Authentication/ValidateOrganizationName",
			{
				method: "POST",
				body: JSON.stringify({ organizationName }),
			}
		);
		return result;
	} catch (e: any) {
		if (e?.isApiError) {
			return { exists: true, message: e.message };
		}
		return { exists: false };
	}
}

export interface SignUpRequest {
	name: string;
	email: string;
	phone: string;
	password: string;
	confirmPassword: string;
	organizationName: string;
	planId?: string;
}

export async function signUpRequest(data: SignUpRequest): Promise<LoginResponse> {
	try {
		const result = await apiFetch<LoginResponse>("/Authentication/SignUp", {
			method: "POST",
			body: JSON.stringify({
				Name: data.name,
				Email: data.email,
				Phone: data.phone,
				Password: data.password,
				ConfirmPassword: data.confirmPassword,
				OrganizationName: data.organizationName,
				PlanId: data.planId,
			}),
		});

		return result;
	} catch (e: any) {
		// Re-lançar com a mensagem apropriada para ser capturada pelo AuthContext
		if (e?.isApiError) {
			return Promise.reject({ message: e.message });
		}
		return Promise.reject({ message: e?.message || "Erro ao criar conta" });
	}
}

export async function updatePassword(
	userId: string,
	currentPassword: string,
	newPassword: string
): Promise<{ success: boolean }> {
	return apiFetch<{ success: boolean }>(`/Authentication/UpdatePassword`, {
		method: "POST",
		auth: true,
		body: JSON.stringify({ userId, currentPassword, newPassword }),
	});
}

export interface BackendUser {
	id: string;
	name: string;
	email: string;
	role: "Admin" | "Manager" | "Sales" | "WhiteLabel";
	managerId?: string;
	teamMembers?: string[];
}

export async function fetchUsers(role?: string): Promise<BackendUser[]> {
	const query = role ? `?role=${encodeURIComponent(role)}` : "";
	return apiFetch<BackendUser[]>(`/users${query}`, { auth: true });
}

export async function fetchLoggedUser(): Promise<LoggedUserRS> {
	return apiFetch<LoggedUserRS>(`/User/GetUserLogged`, { auth: true });
}

export async function fetchSalesUsers(): Promise<BackendUser[]> {
	return apiFetch<BackendUser[]>(`/User/GetSales`, { auth: true });
}

export async function fetchSalesUsersByManager(): Promise<BackendUser[]> {
	return apiFetch<BackendUser[]>(`/User/GetSalesByManager`, { auth: true });
}

// Agents
export interface AgentInfo {
	id: string;
	name: string;
}

export async function fetchAgentName(agentId: string): Promise<AgentInfo> {
	return apiFetch<AgentInfo>(`/Lead/GetAgentName/${agentId}`, { auth: true });
}

export async function fetchAccountAgents(): Promise<AgentInfo[]> {
	return apiFetch<AgentInfo[]>(`/Lead/GetAccountAgents`, { auth: true });
}

// Leads
export interface BackendLead {
	id: string;
	name: string;
	agentId: string;
	assignedTo?: string | null;
	assignedToName?: string | null;
	phoneNumber: string;
	email: string;
	agentPhone?: string | null;
	interested?: string | null;
	integrated: boolean;
	resumeAI: string;
	status: "Novo" | "EmAndamento" | "Finalizado" | string;
	statusId?: string | null;
	leadQuality?: "Neutro" | "Frio" | "Quente";
	creationTime?: string | null;
	observacoes?: Array<{ _id?: string; comentario: string; dataContato: string }>;
	rating?: Rating;
}

export async function fetchLeadsForCurrentUser(): Promise<BackendLead[]> {
	return apiFetch<BackendLead[]>(`/Lead/GetByCurrentUser`, { auth: true });
}

export async function fetchUnassignedLeads(): Promise<BackendLead[]> {
	return apiFetch<BackendLead[]>(`/Lead/GetUnassigned`, { auth: true });
}

export interface UnassignedLeadsQuery {
	skip?: number;
	take?: number;
	quality?: "Frio" | "Neutro" | "Quente" | "all";
	name?: string;
	phone?: string;
	startDate?: string; // ISO date (yyyy-MM-dd or full ISO)
	endDate?: string; // ISO date
}

export async function fetchUnassignedLeadsPaged(params: UnassignedLeadsQuery = {}): Promise<BackendLead[]> {
	const q = new URLSearchParams();
	if (typeof params.skip === "number") q.set("skip", String(params.skip));
	if (typeof params.take === "number") q.set("take", String(params.take));
	if (params.quality && params.quality !== "all") q.set("quality", params.quality);
	if (params.name) q.set("name", params.name);
	if (params.phone) q.set("phone", params.phone);
	if (params.startDate) q.set("startDate", params.startDate);
	if (params.endDate) q.set("endDate", params.endDate);
	const qs = q.toString();
	const url = `/Lead/GetUnassignedPaged${qs ? `?${qs}` : ""}`;
	return apiFetch<BackendLead[]>(url, { auth: true });
}

export interface AssignedLeadsQuery {
	skip?: number;
	take?: number;
	status?: string; // normalized on server
	quality?: "Frio" | "Neutro" | "Quente" | "all";
	sellerId?: string;
	agentId?: string;
	name?: string;
	phone?: string;
	startDate?: string;
	endDate?: string;
}

export async function fetchAssignedLeadsPaged(params: AssignedLeadsQuery = {}): Promise<BackendLead[]> {
	const q = new URLSearchParams();
	if (typeof params.skip === "number") q.set("skip", String(params.skip));
	if (typeof params.take === "number") q.set("take", String(params.take));
	if (params.status) q.set("status", params.status);
	if (params.quality && params.quality !== "all") q.set("quality", params.quality);
	if (params.sellerId) q.set("sellerId", params.sellerId);
	if (params.agentId) q.set("agentId", params.agentId);
	if (params.name) q.set("name", params.name);
	if (params.phone) q.set("phone", params.phone);
	if (params.startDate) q.set("startDate", params.startDate);
	if (params.endDate) q.set("endDate", params.endDate);
	const qs = q.toString();
	const url = `/Lead/GetAssignedPaged${qs ? `?${qs}` : ""}`;
	return apiFetch<BackendLead[]>(url, { auth: true });
}

export async function updateLeadStatus(leadId: string, status: string, statusId?: string): Promise<void> {
	return apiFetch<void>(`/Lead/UpdateStatus/${encodeURIComponent(leadId)}`, {
		method: "PATCH",
		auth: true,
		body: JSON.stringify({ status, statusId }),
	});
}

export async function assignLead(leadId: string, userId: string): Promise<void> {
	return apiFetch<void>(`/Lead/Assign/${encodeURIComponent(leadId)}`, {
		method: "PATCH",
		auth: true,
		body: JSON.stringify({ userId }),
	});
}

export async function updateLeadQuality(leadId: string, leadQuality: "Neutro" | "Frio" | "Quente"): Promise<void> {
	return apiFetch<void>(`/Lead/UpdateQuality/${encodeURIComponent(leadId)}`, {
		method: "PATCH",
		auth: true,
		body: JSON.stringify({ leadQuality }),
	});
}

export async function updateLeadName(leadId: string, name: string): Promise<void> {
	return apiFetch<void>(`/Lead/UpdateName/${encodeURIComponent(leadId)}`, {
		method: "PATCH",
		auth: true,
		body: JSON.stringify({ name }),
	});
}

export async function updateLeadRating(leadId: string, rating: Rating): Promise<void> {
	return apiFetch<void>(`/Lead/UpdateRating/${encodeURIComponent(leadId)}`, {
		method: "PATCH",
		auth: true,
		body: JSON.stringify({ rating }),
	});
}

export async function updateConversationName(conversationId: string, name: string): Promise<void> {
	return apiFetch<void>(`/ChatHistory/UpdateConversationName/${encodeURIComponent(conversationId)}`, {
		method: "PATCH",
		auth: true,
		body: JSON.stringify({ name }),
	});
}

// Observacoes (Lead Notes)
export interface BackendObservacao {
	id: string;
	comentario: string;
	dataContato: string;
}

function mapBackendObservacaoToFrontend(o: BackendObservacao) {
	return { _id: o.id, comentario: o.comentario, dataContato: o.dataContato };
}

export async function getLeadObservacoes(leadId: string) {
	const rs = await apiFetch<BackendObservacao[]>(`/Lead/GetObservacoes/${encodeURIComponent(leadId)}`, {
		auth: true,
	});
	return (rs || []).map(mapBackendObservacaoToFrontend);
}

export async function addLeadObservacao(leadId: string, comentario: string, dataContato?: string) {
	const body: { comentario: string; dataContato?: string } = { comentario };
	if (dataContato) body.dataContato = dataContato;
	const rs = await apiFetch<BackendObservacao>(`/Lead/AddObservacao/${encodeURIComponent(leadId)}`, {
		method: "POST",
		auth: true,
		body: JSON.stringify(body),
	});
	return mapBackendObservacaoToFrontend(rs || ({} as BackendObservacao));
}

export async function updateLeadObservacao(
	leadId: string,
	observacaoId: string,
	comentario: string,
	dataContato?: string
) {
	const body: { comentario: string; dataContato?: string } = { comentario };
	if (dataContato) body.dataContato = dataContato;
	await apiFetch<void>(`/Lead/UpdateObservacao/${encodeURIComponent(leadId)}/${encodeURIComponent(observacaoId)}`, {
		method: "PUT",
		auth: true,
		body: JSON.stringify(body),
	});
}

export async function deleteLeadObservacao(leadId: string, observacaoId: string) {
	await apiFetch<void>(`/Lead/DeleteObservacao/${encodeURIComponent(leadId)}/${encodeURIComponent(observacaoId)}`, {
		method: "DELETE",
		auth: true,
	});
}

// Create Lead
export interface CreateLeadRequest {
	name: string;
	agentId?: string;
	phoneNumber: string;
	email: string;
	agentPhone?: string;
	interested?: string;
	integrated: boolean;
	resumeAI: string;
	status: string;
	statusId?: string;
	leadQuality?: "Neutro" | "Frio" | "Quente";
	observacoes?: any[];
	assignedTo?: string;
	assignedToName?: string;
}

export async function createLead(leadData: CreateLeadRequest): Promise<BackendLead> {
	return apiFetch<BackendLead>("/Lead/Create", {
		method: "POST",
		auth: true,
		body: JSON.stringify(leadData),
	});
}

export async function deleteLead(leadId: string): Promise<void> {
	return apiFetch<void>(`/Lead/Delete/${encodeURIComponent(leadId)}`, {
		method: "DELETE",
		auth: true,
	});
}

export interface PublicLeadViewResponse {
	lead: BackendLead;
	chatHistory: BackendChatHistoryRS | null;
}

export async function getPublicLeadView(
	organizationId: string,
	leadId: string
): Promise<PublicLeadViewResponse> {
	const q = new URLSearchParams();
	q.set("organizationId", organizationId);
	q.set("leadId", leadId);
	return apiFetch<PublicLeadViewResponse>(`/PublicLead/Get?${q.toString()}`, {
		method: "GET",
		auth: false,
	});
}

export interface CampaignUrlCaptureRequest {
	parameters: Record<string, string>;
	fullUrl?: string;
}

export async function saveCampaignUrlCapture(
	data: CampaignUrlCaptureRequest,
	signal?: AbortSignal
): Promise<void> {
	await apiFetch<void>("/PublicCampaignUrl/Save", {
		method: "POST",
		auth: false,
		body: JSON.stringify(data),
		signal,
	});
}

// Chat History
export interface BackendChatMessage {
	role: "assistant" | "user" | string;
	content: string;
	timestamp: string;
}

export type ConversationStatus = "NaoAtendido" | "EmAtendimento" | "Finalizado";

export interface BackendChatHistoryRS {
	id: string;
	agentId: string;
	userId: string;
	name?: string | null;
	type?: string | null;
	messages: BackendChatMessage[];
	randomFollowUp?: boolean;
	pauseIA?: boolean;
	assignedToUserId?: string | null;
	status: ConversationStatus;
}

export async function fetchChatHistoryByUserId(userId: string): Promise<BackendChatHistoryRS | null> {
	try {
		return await apiFetch<BackendChatHistoryRS>(`/ChatHistory/GetByUserId?userId=${encodeURIComponent(userId)}`, {
			auth: true,
		});
	} catch (e) {
		return null;
	}
}

// Conversations API
export interface ConversationFilters {
	name?: string;
	phone?: string;
	startDate?: string;
	endDate?: string;
	agentId?: string;
	assignedToUserId?: string;
	status?: ConversationStatus;
	includeFinished?: boolean;
	skip?: number;
	take?: number;
}

export async function fetchConversations(filters: ConversationFilters = {}): Promise<BackendChatHistoryRS[]> {
	const q = new URLSearchParams();
	if (filters.name) q.set("name", filters.name);
	if (filters.phone) q.set("phone", filters.phone);
	if (filters.startDate) q.set("startDate", filters.startDate);
	if (filters.endDate) q.set("endDate", filters.endDate);
	if (filters.agentId) q.set("agentId", filters.agentId);
	if (filters.assignedToUserId) q.set("assignedToUserId", filters.assignedToUserId);
	if (filters.status) q.set("status", filters.status);
	if (typeof filters.includeFinished === "boolean") q.set("includeFinished", String(filters.includeFinished));
	if (typeof filters.skip === "number") q.set("skip", String(filters.skip));
	if (typeof filters.take === "number") q.set("take", String(filters.take));

	const qs = q.toString();
	const url = `/ChatHistory/GetConversations${qs ? `?${qs}` : ""}`;
	return apiFetch<BackendChatHistoryRS[]>(url, { auth: true });
}

export async function fetchConversationMessages(conversationId: string): Promise<BackendChatMessage[]> {
	return apiFetch<BackendChatMessage[]>(
		`/ChatHistory/GetMessages?conversationId=${encodeURIComponent(conversationId)}`,
		{ auth: true }
	);
}

export async function sendMessage(conversationId: string, content: string): Promise<void> {
	return apiFetch<void>(`/ChatHistory/SendMessage?conversationId=${encodeURIComponent(conversationId)}`, {
		method: "POST",
		auth: true,
		body: JSON.stringify({ content }),
	});
}

export async function sendWhatsAppMessage(conversationId: string, content: string): Promise<void> {
	return apiFetch<void>(`/ChatHistory/SendWhatsAppMessage?conversationId=${encodeURIComponent(conversationId)}`, {
		method: "POST",
		auth: true,
		body: JSON.stringify({ content }),
	});
}

export interface SendWhatsAppImageResponse {
	success: boolean;
	message: string;
	imageUrl: string;
}

export async function sendWhatsAppImage(
	conversationId: string,
	imageBase64: string,
	caption?: string
): Promise<SendWhatsAppImageResponse> {
	return apiFetch<SendWhatsAppImageResponse>(
		`/ChatHistory/SendWhatsAppImage?conversationId=${encodeURIComponent(conversationId)}`,
		{
			method: "POST",
			auth: true,
			body: JSON.stringify({
				imageBase64,
				caption: caption || "",
			}),
		}
	);
}

export interface SuggestResponseRS {
	suggestedResponse: string;
}

export async function suggestResponse(conversationId: string): Promise<SuggestResponseRS> {
	return apiFetch<SuggestResponseRS>(`/ChatHistory/SuggestResponse/${conversationId}`, {
		method: "POST",
		auth: true,
	});
}

export async function assignConversation(conversationId: string, userId: string): Promise<void> {
	return apiFetch<void>(`/ChatHistory/AssignConversation/${encodeURIComponent(conversationId)}`, {
		method: "PATCH",
		auth: true,
		body: JSON.stringify({ userId }),
	});
}

export async function updateConversationStatus(conversationId: string, status: ConversationStatus): Promise<void> {
	return apiFetch<void>(`/ChatHistory/UpdateConversationStatus/${encodeURIComponent(conversationId)}`, {
		method: "PATCH",
		auth: true,
		body: JSON.stringify({ status }),
	});
}

export async function fetchLeadByPhoneNumber(phoneNumber: string): Promise<BackendLead> {
	// Limpar o telefone removendo formatação
	const cleanPhone = phoneNumber.replace(/\D/g, "");
	console.log("Buscando lead por telefone:", cleanPhone);
	return apiFetch<BackendLead>(`/Lead/GetByPhoneNumber?phoneNumber=${encodeURIComponent(cleanPhone)}`, {
		auth: true,
	});
}

// Agent Phones (Atendentes)
export interface AgentPhoneRS {
	id: string;
	agentId: string;
	name?: string;
	phoneNumber: string;
	status: "ativo" | "inativo" | string;
	lastUsed?: string | null;
	attendedBy?: string[];
	timestamp?: string;
}

export interface CreateAgentPhoneRQ {
	agentId: string;
	phoneNumber: string;
	name?: string;
	status?: "ativo" | "inativo";
}

export async function getAgentPhones(agentId: string): Promise<{ agentPhones: AgentPhoneRS[] }> {
	return apiFetch<{ agentPhones: AgentPhoneRS[] }>(`/agentphones/agent/${encodeURIComponent(agentId)}`, {
		auth: true,
	});
}

export async function createAgentPhone(data: CreateAgentPhoneRQ): Promise<AgentPhoneRS> {
	return apiFetch<AgentPhoneRS>(`/agentphones`, { method: "POST", auth: true, body: JSON.stringify(data) });
}

export async function updateAgentPhone(id: string, data: CreateAgentPhoneRQ): Promise<AgentPhoneRS> {
	return apiFetch<AgentPhoneRS>(`/agentphones/${encodeURIComponent(id)}`, {
		method: "PUT",
		auth: true,
		body: JSON.stringify(data),
	});
}

export async function deleteAgentPhone(id: string): Promise<void> {
	return apiFetch<void>(`/agentphones/${encodeURIComponent(id)}`, { method: "DELETE", auth: true });
}

// Agent Users (New many-to-many relationship)
export interface AgentUserRS {
	id: string;
	agentId: string;
	userId: string;
	role: "Owner" | "Manager" | "User" | "Viewer";
	assignedAt: string;
	assignedBy: string;
	isActive: boolean;
	userName?: string;
	userUsername?: string;
	userPhone?: string;
	agentName?: string;
}

export interface CreateAgentUserRQ {
	agentId: string;
	userId: string;
	role: "Owner" | "Manager" | "User" | "Viewer";
	isActive?: boolean;
}

export interface UpdateUserRoleRQ {
	role: "Owner" | "Manager" | "User" | "Viewer";
}

export async function getAgentUsers(agentId: string): Promise<{ agentUsers: AgentUserRS[] }> {
	return apiFetch<{ agentUsers: AgentUserRS[] }>(`/agentusers/GetByAgentId/${encodeURIComponent(agentId)}`, {
		auth: true,
	});
}

// Note: Many-to-many relationship - users can be assigned to multiple agents
// This endpoint returns the first active agent assignment (kept for backwards compatibility)
export async function getUserAgent(userId: string): Promise<AgentUserRS | null> {
	try {
		const response = await apiFetch<{ agentUsers: AgentUserRS[] }>(
			`/agentusers/GetByUserId/${encodeURIComponent(userId)}`,
			{ auth: true }
		);
		return (response?.agentUsers || []).find((au) => au.isActive) || null;
	} catch {
		return null;
	}
}

// Get all active agent assignments for a user
export async function getUserAgents(userId: string): Promise<AgentUserRS[]> {
	try {
		const response = await apiFetch<{ agentUsers: AgentUserRS[] }>(
			`/agentusers/GetByUserId/${encodeURIComponent(userId)}`,
			{ auth: true }
		);
		return (response?.agentUsers || []).filter((au) => au.isActive);
	} catch {
		return [];
	}
}

// Get all active agent users (for filtering and assignment purposes)
export async function getAllActiveAgentUsers(): Promise<AgentUserRS[]> {
	try {
		const response = await apiFetch<{ agentUsers: AgentUserRS[] }>(`/agentusers/GetAllActive`, { auth: true });
		return response?.agentUsers || [];
	} catch {
		return [];
	}
}

export async function assignUserToAgent(data: CreateAgentUserRQ): Promise<AgentUserRS> {
	return apiFetch<AgentUserRS>(`/agentusers/AssignUserToAgent`, {
		method: "POST",
		auth: true,
		body: JSON.stringify(data),
	});
}

export async function updateUserRole(agentUserId: string, data: UpdateUserRoleRQ): Promise<AgentUserRS> {
	return apiFetch<AgentUserRS>(`/agentusers/UpdateUserRole/${encodeURIComponent(agentUserId)}/role`, {
		method: "PUT",
		auth: true,
		body: JSON.stringify(data),
	});
}

export async function removeUserFromAgent(agentUserId: string): Promise<void> {
	return apiFetch<void>(`/agentusers/RemoveUserFromAgent/${encodeURIComponent(agentUserId)}`, {
		method: "DELETE",
		auth: true,
	});
}

// Agents
export interface BackendAgent {
	id: string;
	name: string;
	description: string;
	status: "Enabled" | "Disabled";
	userId: string;
	whatsAppConnected: string;
}

export async function fetchAgents(): Promise<BackendAgent[]> {
	return apiFetch<BackendAgent[]>("/agent/GetAgentsWithPagination?pageIndex=1&pageSize=1000", { auth: true });
}

// Plans API
export async function fetchPlans(): Promise<Plan[]> {
	return apiFetch<Plan[]>("/Plan/GetAll", { method: "GET" });
}

export async function fetchPlanById(id: string): Promise<Plan> {
	return apiFetch<Plan>(`/Plan/Get/${id}`, { method: "GET" });
}

// Payment API
export async function processPayment(paymentData: PaymentRequestData): Promise<PaymentResponseData> {
	return apiFetch<PaymentResponseData>("/Payment/ProcessPayment", {
		method: "POST",
		body: JSON.stringify(paymentData),
		auth: true,
	});
}

export async function changePlan(
	paymentData: PaymentRequestData
): Promise<{ success: boolean; message: string; paymentResult?: PaymentResponseData; organization?: Organization }> {
	return apiFetch<{
		success: boolean;
		message: string;
		paymentResult?: PaymentResponseData;
		organization?: Organization;
	}>("/Payment/ChangePlan", {
		method: "POST",
		body: JSON.stringify(paymentData),
		auth: true,
	});
}

// Payment API
export interface Payment {
	id: string;
	organizationId: string;
	asaasPaymentId: string;
	amount: number;
	currency: string;
	status: string;
	description: string;
	dueDate: string;
	confirmedDate?: string;
	processedAt: string;
	planId?: string;
	creditsAmount?: number;
	paymentType: string;
	invoiceUrl?: string;
	transactionReceiptUrl?: string;
}

export async function fetchPayments(organizationId?: string): Promise<Payment[]> {
	const query = organizationId ? `?organizationId=${organizationId}` : "";
	return apiFetch<Payment[]>(`/Payment/GetByOrganizationId${query}`, {
		method: "GET",
		auth: true,
	});
}

// Payment History API (mantido para compatibilidade)
export interface PaymentHistory {
	id: string;
	organizationId: string;
	invoiceNumber: string;
	amount: number;
	status: "paid" | "pending" | "failed" | "cancelled";
	dueDate: string;
	paidDate?: string;
	planName: string;
	paymentMethod: string;
	description: string;
	transactionId: string;
	createdAt: string;
	updatedAt?: string;
	failureReason?: string;
	processedBy?: string;
}

export async function fetchPaymentHistory(organizationId?: string): Promise<PaymentHistory[]> {
	const query = organizationId ? `?organizationId=${organizationId}` : "";
	return apiFetch<PaymentHistory[]>(`/PaymentHistory/GetPaymentHistory${query}`, {
		method: "GET",
		auth: true,
	});
}

export async function updatePaymentStatus(
	paymentId: string,
	status: string,
	paidDate?: string,
	failureReason?: string,
	transactionId?: string
): Promise<PaymentHistory> {
	return apiFetch<PaymentHistory>(`/PaymentHistory/UpdatePaymentStatus/${paymentId}`, {
		method: "PATCH",
		auth: true,
		body: JSON.stringify({
			status,
			paidDate,
			failureReason,
			transactionId,
		}),
	});
}

export async function savePaymentHistory(paymentData: {
	organizationId: string;
	invoiceNumber: string;
	amount: number;
	status: string;
	dueDate: string;
	paidDate?: string;
	planName: string;
	paymentMethod: string;
	description: string;
	transactionId: string;
	failureReason?: string;
	processedBy?: string;
}): Promise<PaymentHistory> {
	return apiFetch<PaymentHistory>("/Payment/SavePaymentHistory", {
		method: "POST",
		auth: true,
		body: JSON.stringify(paymentData),
	});
}

// Interface para resposta do logo por domínio
export interface LogoByDomainResponse {
	logoUrl?: string;
	domain?: string;
	organizationName?: string;
}

// Função para buscar logo por domínio
export async function getLogoByDomain(domain: string): Promise<LogoByDomainResponse | null> {
	try {
		const result = await apiRequest<LogoByDomainResponse>(
			`/OrganizationSettings/GetLogoByDomain/${encodeURIComponent(domain)}`,
			{
				method: "GET",
				auth: false, // Não precisa de autenticação para buscar logo
			}
		);

		if (result.success) {
			return result.data || null;
		}

		return null;
	} catch (error) {
		console.error("Error fetching logo by domain:", error);
		return null;
	}
}

// API client object for HTTP methods
export const api = {
	get: <T>(url: string, options?: ApiOptions) => apiFetch<T>(url, { ...options, method: "GET" }),
	post: <T>(url: string, data?: any, options?: ApiOptions) =>
		apiFetch<T>(url, { ...options, method: "POST", body: data ? JSON.stringify(data) : undefined }),
	put: <T>(url: string, data?: any, options?: ApiOptions) =>
		apiFetch<T>(url, { ...options, method: "PUT", body: data ? JSON.stringify(data) : undefined }),
	patch: <T>(url: string, data?: any, options?: ApiOptions) =>
		apiFetch<T>(url, { ...options, method: "PATCH", body: data ? JSON.stringify(data) : undefined }),
	delete: <T>(url: string, options?: ApiOptions) => apiFetch<T>(url, { ...options, method: "DELETE" }),
};

export interface Rating {
	agentId: string;
	phone: string;
	document?: string | null;
	name?: string | null;
	birthDate?: string | null;
	age?: number | null;
	personalMaritalStatus?: string | null;
	companyBusinessSizeOriginal?: string | null;
	creditScoreD00?: string | null;
	incomePersonal?: string | null;
	incomePartner?: string | null;
	incomeFamily?: string | null;
	revenueShared?: string | null;
	revenuePresumed?: string | null;
	revenueBalance?: string | null;
	vehicles?: number | null;
	personalClass?: number | null;
	gender?: string | null;
	sequentialId?: string | null;
	businessPotencial?: number | null;
	log?: string | null;
}

// Temporary signup data API
export interface TemporarySignupData {
	sessionId: string;
	name?: string;
	email?: string;
	phone?: string;
}

export async function saveTemporarySignupData(data: TemporarySignupData): Promise<{ success: boolean; message?: string }> {
	try {
		const result = await apiRequest<{ success: boolean; message?: string }>("/Authentication/SaveTemporarySignupData", {
			method: "POST",
			body: JSON.stringify(data),
			auth: false, // Não precisa de autenticação para salvar dados temporários
		});
		return result.success ? { success: true, message: result.data?.message } : { success: false, message: result.error };
	} catch (e: any) {
		return { success: false, message: e?.message || "Erro ao salvar dados temporários" };
	}
}
